import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducer';
import { Button, Paper, Checkbox } from '../../../tailwindcomponents';
import { Typography, Table, TableRow, TableCell } from './../../../tailwindcomponents';
import { ToolData } from '../../../types/tool';
import { EmployeeSearch, ProfileImg } from './../../../components';
import toolsApi from './../../../api/tools';
import { Datepicker } from '@mobiscroll/react';
import '@mobiscroll/react/dist/css/mobiscroll.min.css';

interface ToolsLateReturnsProps {
  setLoading: (loading: boolean) => void;
}

function ToolsLateReturns({ setLoading }: ToolsLateReturnsProps) {
  const store = useSelector((state: RootState) => state);
  const [filtered_tools, setFilteredTools] = useState<ToolData[]>([]);
  const [late_return_tools_for_employee, setLateReturnsForEmployee] = useState<ToolData[]>([]);
  const [order_by_text, setOrderByText] = useState("Created date");
  const [ascending, setAscending] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [fromDate, setFromDate] = useState<string | null>("2026-04-17T00:00:00.000Z");
  const [toDate, setToDate] = useState<string | null>(new Date().toISOString());
  const [searchKey, setSearchKey] = useState<number>(0);
  const users = useSelector((state: RootState) => state.employees.all_employees);

  const hasDateFilters = Boolean(fromDate && toDate);

  // Format date for API: extract YYYY-MM-DD from ISO or Date object
  const formatDateForApi = (date: string): string => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  useEffect(() => {
    setLoading(true);
    if (hasDateFilters) {
      const from = formatDateForApi(fromDate!);
      const to = formatDateForApi(toDate!);
      toolsApi.getLateToolReturns(order_by_text, ascending, 0, from, to).then(res => {
        setFilteredTools(res);
      }).finally(() => setLoading(false));
    } else {
      toolsApi.getLateToolReturns(order_by_text, ascending, 0).then(res => {
        setFilteredTools(res);
      }).finally(() => setLoading(false));
    }
  }, [order_by_text, ascending, fromDate, toDate]);

  // Load detail data when an employee is selected
  useEffect(() => {
    if (selectedEmployeeId && selectedEmployeeId > 0) {
      setLoading(true);
      if (hasDateFilters) {
        const from = formatDateForApi(fromDate!);
        const to = formatDateForApi(toDate!);
        toolsApi.getLateToolReturnsByEmployee(order_by_text, ascending, selectedEmployeeId, from, to).then(res => {
          setLateReturnsForEmployee(res);
        }).finally(() => setLoading(false));
      } else {
        toolsApi.getLateToolReturnsByEmployee(order_by_text, ascending, selectedEmployeeId).then(res => {
          setLateReturnsForEmployee(res);
        }).finally(() => setLoading(false));
      }
    } else {
      setLateReturnsForEmployee([]);
    }
  }, [selectedEmployeeId, order_by_text, ascending, fromDate, toDate]);
  
  const selectedEmployeeName = selectedEmployeeId
    ? store.employees.all_employees.find(s => s.Id === selectedEmployeeId)?.employee_name || "Unknown"
    : null;

  const generateDetailTableBody = (row: ToolData, index: number) => {
    return (
      <TableRow key={row.loaned_out_tool_id}>
        <TableCell>{index}</TableCell>
        <TableCell>{row.created_date_text}</TableCell>
        <TableCell>{row.description}</TableCell>
        <TableCell>{row.bay}</TableCell>
        <TableCell>{row.area}</TableCell>
        <TableCell>{row.out_date_text}</TableCell>
        <TableCell>{row.in_date_text}</TableCell>
        <TableCell>
          {row.extended_loan === true
            ? <Checkbox color="success" checked={true} />
            : ""}
        </TableCell>
      </TableRow>
    );
  };

  const generateMasterTableBody = (row: ToolData, index: number) => {
    const isSelected = selectedEmployeeId === row.employee_id;
    return (
      <TableRow
        key={index}
        onClick={() => onRowClick(row)}
        className={isSelected ? "bg-black/[0.08] cursor-pointer" : "cursor-pointer hover:bg-black/[0.04]"}
      >
        <TableCell>{index}</TableCell>
        <TableCell className="flex items-center gap-2">
          <ProfileImg
            imgKey={index}
            imgSize="xs"
            userName={users.find(s => s.Id === row.employee_id)?.userName?.split('@')[0]}
          />
          <Typography align="left" text={row.employee_name} display="inline" />
        </TableCell>
        <TableCell>{row.late_returns_counter}</TableCell>
      </TableRow>
    );
  };

  const onRowClick = (row: ToolData) => {
    setSelectedEmployeeId(row.employee_id);
  };

  const OnOrderByTitles = (given_order_by_text: string) => {
    const temp_ascending = order_by_text === given_order_by_text ? !ascending : true;
    setAscending(temp_ascending);
    setOrderByText(given_order_by_text);
  };

  const onSelectedEmployeeRow = (employee_id: number) => {
    setSelectedEmployeeId(employee_id);
  };

  const onClearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedEmployeeId(null);
    setLateReturnsForEmployee([]);
    setOrderByText("Created date");
    setAscending(false);
    setSearchKey(prev => prev + 1);
  };

  return (
    <div className="w-full">
      <Typography text={"Search for users history"} align="center" variant="h2" />
      <Typography text={`Total available (${filtered_tools.length})`} align="center" variant="body1" />

      <div className="mb-4 flex flex-wrap justify-center items-end gap-2">
        <div className="w-full sm:w-1/2 md:w-1/4">
          <EmployeeSearch
            key={searchKey}
            place_holder={"Write employees name"}
            onSelectedEmployee={(id: number) => onSelectedEmployeeRow(id)}
          />
        </div>
        <div className="min-w-[140px] flex items-end [&_.mbsc-textfield-wrapper]:mb-0">
          <Datepicker
            label="From"
            value={fromDate}
            onChange={(e) => setFromDate(e.value as string | null)}
            inputProps={{ placeholder: 'From date' }}
            controls={['calendar']}
            dateFormat="YYYY-MM-DD"
            returnFormat="iso8601"
            touchUi={false}
          />
        </div>
        <div className="min-w-[140px] flex items-end [&_.mbsc-textfield-wrapper]:mb-0">
          <Datepicker
            label="To"
            value={toDate}
            onChange={(e) => setToDate(e.value as string | null)}
            inputProps={{ placeholder: 'To date' }}
            controls={['calendar']}
            dateFormat="YYYY-MM-DD"
            returnFormat="iso8601"
            touchUi={false}
          />
        </div>
        <div className="flex items-end pb-1">
          <Button
            variant="outlined"
            size="small"
            onClick={onClearFilters}
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="flex gap-4 w-full min-h-[70vh]">
        {/* Left: Employee table */}
        <Paper variant="outlined" className="flex-[0_0_30%] min-w-0">
          <Table
            headers={["Name", "Late returns"]}
            rows={filtered_tools}
            generateBody={(row: any, index: number) => generateMasterTableBody(row, index)}
            defaultRowsPerPage={100}
            OnOrderByTitles={(text: string) => OnOrderByTitles(text)}
          />
        </Paper>

        {/* Right: Detail panel */}
        <Paper variant="outlined" className="flex-1 min-w-0 overflow-y-auto max-h-[80vh] p-4">
          {selectedEmployeeId ? (
            <>
              <Typography
                text={`History for late returns — ${selectedEmployeeName}`}
                align="left"
                variant="h4"
                fontWeight={400}
              />
              <Table
                headers={["Created date", "Description", "Bay", "Area", "Loaned out", "Returned", "Extended loan"]}
                rows={late_return_tools_for_employee}
                generateBody={(row: any, index: number) => generateDetailTableBody(row, index)}
                defaultRowsPerPage={100}
                OnOrderByTitles={(text: string) => OnOrderByTitles(text)}
              />
            </>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[300px] text-gray-500">
              <Typography text="Select an employee to view details" align="center" variant="body1" />
            </div>
          )}
        </Paper>
      </div>
    </div>
  );
}

export default ToolsLateReturns;